import {useNavigate, useSearchParams} from "react-router-dom";
import {useEffect} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import toast from "react-hot-toast";
import {
  ReactComponent as JayapranaLogo
} from '@/assets/icons/jayaprana-logo.svg'
import './index.css'
import BankInput from "@/pages/Refund/BankInput";
import Button from "@/components/common/Button";
import Refund from "@/pages/Refund";
import {RootState, useAppDispatch} from "@/store";
import {
  getRefundCustomerDetail,
  submitRefundCustomerDetail
} from "@/store/refund-form/action";
import Invoice from "@/pages/Invoice";
import {getShortDateFormat, hourFormat} from "@/utils";

const initialRefundValue = {
  note: '',
  total_price: 0,
  is_manual_input: false,
  details: [],
  customer_info: {
    account_name: '',
    bank_code: '',
    account_number: ''
  }
} as Refund

const renderBookingDetail = (id: InvoiceBookingDetail[]) => {
  const bookingByDate = {}
  id.forEach((bd) => {
    if (bookingByDate[bd.date]) {
      bookingByDate[bd.date].push(bd)
    } else {
      bookingByDate[bd.date] = [bd]
    }
  })
  return Object.keys(bookingByDate).map((key) =>
    <div className="text-jsm j-col gap-1">
      <div className="font-bold">
        {getShortDateFormat(parseInt(key, 10))}
      </div>
      <ul className="list-disc ml-5">
        {bookingByDate[key].map(b =>
          <li>
            {hourFormat(b?.start_time ?? 0)} - {hourFormat(b?.end_time ?? 0)}
          </li>)}
      </ul>
    </div>
  )
}

const renderBooking = (iDetails: InvoiceBookingDetail[]) => {
  const bookingByDate = {}
  iDetails.forEach((bd) => {
    if (bookingByDate[bd?.venue_court?.code]) {
      bookingByDate[bd?.venue_court?.code].push(bd)
    } else {
      bookingByDate[bd?.venue_court?.code] = [bd]
    }
  })

  return Object.keys(bookingByDate).map((key) =>
    <>
      <div className="booking-detail-title">Booking date</div>
      <div className="booking-detail-value j-col gap-2">
        {renderBookingDetail(bookingByDate[key])}
      </div>
      <div className="booking-detail-title">Court</div>
      <div className="booking-detail-value font-bold">{key}</div>
    </>
  )
}

const CustomerRefund = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {
    refund,
    successSubmit,
    expiredLink
  } = useSelector((rootState: RootState) => rootState.refundForm.state)
  const isLoading = useSelector((rootState: RootState) => rootState.refundForm.loading)
  const [searchParams] = useSearchParams()
  const refundInfoMethods = useForm({
    defaultValues: initialRefundValue
  })
  const {reset, getValues, watch} = refundInfoMethods
  const refundReason = watch('note')
  const invoice = watch('invoice') as Invoice
  const bookingId = invoice?.booking?.custom_id ?? ''
  const customer = watch('customer') as RefundCustomer
  const bankValue = watch('customer_info.bank_code')
  const bankSelectValue = bankValue ? {
    label: bankValue,
    value: bankValue
  } : null
  const refundDetails = (watch('details') ?? []) as RefundDetail[]
  const invoiceDetails = refundDetails.map(r => r?.invoice_detail)
  const bookingDetails = invoiceDetails
    .map(i => i.booking_detail)
    .filter(i => i.venue_court)
  // const venueCourts = bookingDetails.map(b => b?.venue_court?.code).filter(v => v)
  // const uniqueCourts = new Set(venueCourts)
  // const uniqueCourtText = new Array(...uniqueCourts).join(', ')

  const validateInput = () => {
    const customerInfo = getValues('customer_info')
    return customerInfo.account_name &&
      customerInfo.bank_code &&
      customerInfo.account_number
  }

  const wrongToken = () => {
    // toast.error('Link has expired')
  }

  const failedSubmitRefundCustomerDetail = (message: string) => {
    toast.error(message)
    navigate('/')
  }

  const successSubmitRefundCustomerDetail = () => {
    toast.success('Success submit data')
  }

  const performSubmitCustomerInfo = () => {
    const param = {
      refundId: searchParams.get('ri'),
      token: searchParams.get('t'),
      body: getValues('customer_info'),
      success: successSubmitRefundCustomerDetail,
      fail: failedSubmitRefundCustomerDetail
    }
    dispatch(submitRefundCustomerDetail(param))
  }

  const onClickSubmit = () => {
    if (!validateInput) {
      return
    }
    performSubmitCustomerInfo()
  }

  useEffect(() => {
    reset(refund)
  }, [refund])

  useEffect(() => {
    const param = {
      refundId: searchParams.get('ri'),
      token: searchParams.get('t'),
      fail: wrongToken
    }
    dispatch(getRefundCustomerDetail(param))
  }, [])

  return <FormProvider {...refundInfoMethods}>
    <div className="relative w-full j-col justify-center items-center">
      <div className="absolute top-0 w-full bg-black-primary h-[30vh]"/>
      <div
        className="z-10 absolute flex flex-col gap-8 justify-start items-center top-8 max-w-[640px] w-full mx-auto">
        <div className="h-[48px] w-auto text-white">
          <JayapranaLogo/>
        </div>
        <div
          className="w-full bg-white py-8 px-12 j-col rounded j-box-shadow gap-3">
          <div className="j-col gap-8">
            <div className="text-j2xl text-center">REFUND FORM</div>
            <div className="horizontal-separator--dash"/>
          </div>
          {
            isLoading ? <></> :
              <>
                {
                  expiredLink ? <div className="text-base text-jsm text-center">
                      This link has expired
                    </div>
                    : <>
                      {
                        successSubmit ? <div className="text-base text-jsm text-center">
                            Success submit data, you can close this window now
                          </div>
                          : <>
                            <div className="j-col gap-3 mt-3">
                              <div
                                className="j-base font-bold">Hi, {customer?.name ?? ''}</div>
                              <div className="text-jsm">Mohon maaf pesanan anda kami ajukan proses refund.
                              </div>
                              <div className="j-row gap-5">
                                <div className="border-l-red-secondary border-l-4"/>
                                <div className="grow j-col gap-1">
                                  <div
                                    className="text-base font-medium text-red-secondary">Refund
                                    Reason
                                  </div>
                                  <div className="text-jsm">{refundReason}</div>
                                </div>
                              </div>
                              <div className="horizontal-separator--dash"/>
                            </div>
                            <div className="booking-detail">
                              <div className="booking-detail-title">Booking ID</div>
                              <div
                                className="booking-detail-value font-bold">{bookingId}</div>
                              {renderBooking(bookingDetails)}
                              <div className="booking-detail-title">Name</div>
                              <div
                                className="booking-detail-value">{customer?.name ?? ''}</div>
                              <div className="booking-detail-title">Phone</div>
                              <div
                                className="booking-detail-value">{customer?.phone_number ?? ''}</div>
                            </div>
                            <div className="horizontal-separator--dash"/>
                            <div className="j-col gap-2">
                              <div className="text-jsm">Mohon untuk isi form
                                pengembalian
                                dana di bawah ini:
                              </div>
                              <BankInput selectedBank={bankSelectValue}/>
                            </div>
                            <Button
                              type="submit"
                              variant="primary"
                              className="text-white"
                              onClick={onClickSubmit}
                            >
                              Submit
                            </Button>
                          </>
                      }
                    </>
                }
              </>
          }
        </div>
      </div>
    </div>
  </FormProvider>
}

export default CustomerRefund;
